import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { noteAnalyticsAPI } from '../services/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { formatDuration } from '../utils/formatters';
import { FiSearch, FiRefreshCw, FiAlertTriangle, FiPhoneIncoming, FiPhoneOutgoing, FiChevronLeft, FiChevronRight } from 'react-icons/fi';

// Operatorlar qo'lda yozgan izohlar tahlili. Hisob-kitob SERVERDA (routes/noteAnalytics.js),
// mavzular lug'ati services/noteThemes.js da — bu sahifa faqat ko'rsatadi.
// Har 60 soniyada o'zi yangilanadi: kun davomida izohlar qo'shilib boradi.

const PERIODS = [
  { v: '7', t: '7 kun' },
  { v: '30', t: '30 kun' },
  { v: '90', t: '90 kun' },
  { v: 'all', t: 'Hammasi' },
];

const BAR_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444', '#06b6d4',
  '#ec4899', '#84cc16', '#f97316', '#6366f1'];

const PER_PAGE = 20;   // izohlar ro'yxati shuncha qatordan sahifalanadi

export default function NoteAnalyticsPage() {
  const [days, setDays] = useState('30');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [theme, setTheme] = useState(null);
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [updatedAt, setUpdatedAt] = useState(null);

  const load = useCallback(async (silent) => {
    if (!silent) setLoading(true);
    try {
      const res = await noteAnalyticsAPI.get({ days });
      setData(res.data);
      setError(null);
      setUpdatedAt(new Date());
    } catch (err) {
      // 404 = backend hali yangi marshrutni bilmaydi (restart qilinmagan).
      setError(err.response?.status === 404 ? 'restart' : (err.response?.data?.error || 'Tahlilni olishda xato'));
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    load();
    const t = setInterval(() => load(true), 60000);
    return () => clearInterval(t);
  }, [load]);

  const notes = useMemo(() => {
    if (!data) return [];
    const q = query.trim().toLowerCase();
    return data.notes.filter((n) => {
      if (theme && n.theme !== theme) return false;
      if (!q) return true;
      return (n.text || '').toLowerCase().includes(q)
        || (n.contact || '').toLowerCase().includes(q)
        || (n.number || '').includes(q)
        || (n.operator || '').toLowerCase().includes(q);
    });
  }, [data, theme, query]);

  useEffect(() => { setPage(1); }, [theme, query, days]);

  if (loading) return <div className="loading-center"><div className="spinner" /></div>;

  if (error === 'restart') {
    return (
      <div>
        <Header days={days} setDays={setDays} onReload={() => load()} updatedAt={null} />
        <div className="card" style={{ borderLeft: '4px solid #f59e0b' }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: 4 }}>
            <FiAlertTriangle size={22} color="#f59e0b" style={{ flexShrink: 0, marginTop: 2 }} />
            <div>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>Backend hali yangilanmagan</div>
              <div style={{ color: 'var(--text-secondary)', fontSize: 14, lineHeight: 1.6 }}>
                Tahlil bo'limi yangi server marshrutidan foydalanadi. U ishga tushishi uchun
                serverda bir marta <code style={codeStyle}>sudo pm2 restart server</code> bajarilishi kerak.
                Shundan keyin shu sahifani yangilang.
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <Header days={days} setDays={setDays} onReload={() => load()} updatedAt={null} />
        <div className="card" style={{ color: '#ef4444' }}>{error}</div>
      </div>
    );
  }

  const { totals, themes, operators, daily, top_contacts: topContacts, quality, range } = data;
  const totalPages = Math.max(1, Math.ceil(notes.length / PER_PAGE));
  const curPage = Math.min(page, totalPages);
  const pageNotes = notes.slice((curPage - 1) * PER_PAGE, curPage * PER_PAGE);
  const maxTheme = themes.length ? themes[0].count : 1;
  const hints = buildHints(totals, quality, themes);

  return (
    <div>
      <Header days={days} setDays={setDays} onReload={() => load()} updatedAt={updatedAt} />

      {totals.notes === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
          Bu davrda izoh yozilmagan.
        </div>
      ) : (
        <>
          <div className="stats-grid" style={{ marginBottom: 24 }}>
            <Stat label="Izohlar" value={totals.notes} hint={`${range.from} — ${range.to}`} />
            <Stat label="Qamrov" value={`${totals.coverage_pct}%`}
              hint={`${totals.total_calls} qo'ng'iroqdan`} />
            <Stat label="Kiruvchi" value={`${totals.inbound_pct}%`} hint={`${totals.inbound} ta izoh`} />
            <Stat label="O'rtacha suhbat" value={formatDuration(totals.avg_talk_noted_sec)}
              hint={`hammasi: ${formatDuration(totals.avg_talk_all_sec)}`} />
            <Stat label="Jami suhbat" value={`${Math.round(totals.talk_total_sec / 3600)} soat`}
              hint={`${totals.operators} xodim`} />
          </div>

          {hints.length > 0 && (
            <div className="card" style={{ marginBottom: 20, borderLeft: '4px solid #f59e0b' }}>
              <div className="card-header"><span className="card-title">E'tibor bering</span></div>
              <ul style={{ margin: 0, paddingLeft: 20, color: 'var(--text-secondary)', fontSize: 14, lineHeight: 1.8 }}>
                {hints.map((h, i) => <li key={i}>{h}</li>)}
              </ul>
            </div>
          )}

          <div className="adm-g2" style={{ gap: 20, marginBottom: 20 }}>
            <div className="card">
              <div className="card-header">
                <span className="card-title">Mavzular</span>
                {theme && (
                  <button className="btn btn-ghost btn-sm" onClick={() => setTheme(null)}>Filtrni olib tashlash</button>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {themes.map((t, i) => {
                  const on = theme === t.key;
                  return (
                    <button key={t.key} type="button" onClick={() => setTheme(on ? null : t.key)}
                      title="Shu mavzudagi izohlarni ko'rish"
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10, width: '100%', cursor: 'pointer',
                        background: on ? 'var(--accent-light, #3b82f620)' : 'transparent',
                        border: '1px solid ' + (on ? '#3b82f6' : 'transparent'),
                        borderRadius: 8, padding: '6px 8px', textAlign: 'left',
                      }}>
                      <span style={{ width: 170, fontSize: 13, color: 'var(--text-primary)' }}>{t.label}</span>
                      <span style={{ flex: 1, height: 10, background: 'var(--bg-primary,#0f172a)', borderRadius: 6, overflow: 'hidden' }}>
                        <span style={{
                          display: 'block', height: '100%', width: `${Math.round((t.count / maxTheme) * 100)}%`,
                          background: BAR_COLORS[i % BAR_COLORS.length],
                        }} />
                      </span>
                      <span style={{ width: 74, textAlign: 'right', fontSize: 13, color: 'var(--text-muted)' }}>
                        {t.count} · {t.pct}%
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="card">
              <div className="card-header"><span className="card-title">Kunlik izohlar</span></div>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={daily}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2d3a4f" />
                  <XAxis dataKey="date" tick={{ fill: '#64748b', fontSize: 10 }}
                    tickFormatter={(d) => d.slice(5)} interval="preserveStartEnd" minTickGap={18} />
                  <YAxis tick={{ fill: '#64748b', fontSize: 11 }} allowDecimals={false} />
                  <Tooltip contentStyle={tooltipStyle} labelFormatter={(d) => d} formatter={(v) => [v, 'izoh']} />
                  <Bar dataKey="count" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="adm-g2" style={{ gap: 20, marginBottom: 20 }}>
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <div className="card-header" style={{ padding: 16 }}><span className="card-title">Xodimlar</span></div>
              <div className="table-wrapper">
                <table>
                  <thead>
                    <tr><th>Xodim</th><th>Izoh</th><th>O'rtacha uzunlik</th><th>Suhbat</th><th>Ko'p mavzu</th></tr>
                  </thead>
                  <tbody>
                    {operators.map((o) => (
                      <tr key={o.name}>
                        <td style={{ fontWeight: 600 }}>{o.name}</td>
                        <td>{o.count}</td>
                        <td>{o.avg_len} belgi</td>
                        <td>{Math.round(o.talk_sec / 3600)} soat</td>
                        <td style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                          {o.top_theme} ({o.top_theme_count})
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <div className="card-header" style={{ padding: 16 }}>
                <span className="card-title">Ko'p murojaat qilganlar</span>
              </div>
              <div className="table-wrapper">
                <table>
                  <thead><tr><th>Mijoz</th><th>Raqam</th><th>Izoh</th></tr></thead>
                  <tbody>
                    {topContacts.length === 0 ? (
                      <tr><td colSpan="3" style={emptyCell}>Takroriy murojaat yo'q</td></tr>
                    ) : topContacts.map((c) => (
                      <tr key={c.number}>
                        <td style={{ fontWeight: 600 }}>{c.name || '—'}</td>
                        <td style={{ fontFamily: 'monospace', fontSize: 13 }}>{c.number}</td>
                        <td>{c.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="card" style={{ marginBottom: 20 }}>
            <div className="card-header"><span className="card-title">Yozuv sifati</span></div>
            <div className="adm-g3" style={{ gap: 16 }}>
              <QualityBox title="Alifbo"
                main={`${quality.cyrillic_pct}% kirill`}
                sub={`${quality.cyrillic} kirill · ${quality.latin} lotin. Aralash yozuv qidiruvni buzadi.`} />
              <QualityBox title="Juda qisqa izohlar"
                main={`${quality.short_count} ta`}
                sub={quality.short_examples.length
                  ? quality.short_examples.map((s) => `«${s}»`).join(', ')
                  : 'Yo’q'} />
              <QualityBox title="Klaviatura tili xato"
                main={`${quality.layout_bad_count} ta`}
                sub={quality.layout_bad_count ? 'Pastda o’qiladigan ko’rinishi berilgan.' : 'Yo’q'} />
            </div>
            {quality.layout_bad.length > 0 && (
              <div style={{ marginTop: 14, borderTop: '1px solid var(--border,#2d3a4f)', paddingTop: 12 }}>
                {quality.layout_bad.map((n) => (
                  <div key={n.id} style={{ fontSize: 13, marginBottom: 8 }}>
                    <span style={{ color: 'var(--text-muted)' }}>{n.date} {n.time} · {n.operator} — </span>
                    <span style={{ fontFamily: 'monospace' }}>{n.text}</span>
                    <div style={{ color: '#f59e0b', marginLeft: 12 }}>→ {n.decoded}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div className="card-header" style={{ padding: 16, gap: 12, flexWrap: 'wrap' }}>
              <span className="card-title">
                Izohlar{theme ? ` · ${themes.find((t) => t.key === theme)?.label}` : ''} ({notes.length})
              </span>
              <div style={{ position: 'relative', minWidth: 240 }}>
                <FiSearch size={15} style={{ position: 'absolute', left: 10, top: 11, color: 'var(--text-muted)' }} />
                <input className="input" style={{ paddingLeft: 32 }} value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Izoh, mijoz, raqam yoki xodim..." />
              </div>
            </div>
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr><th>Sana</th><th></th><th>Mijoz</th><th>Xodim</th><th>Vaqt</th><th>Mavzu</th><th>Izoh</th></tr>
                </thead>
                <tbody>
                  {pageNotes.length === 0 ? (
                    <tr><td colSpan="7" style={emptyCell}>Mos izoh topilmadi</td></tr>
                  ) : pageNotes.map((n) => (
                    <tr key={n.id}>
                      <td style={{ whiteSpace: 'nowrap', fontSize: 13 }}>{n.date.slice(5)} {n.time}</td>
                      <td title={n.direction === 'inbound' ? 'Kiruvchi' : 'Chiquvchi'}>
                        {n.direction === 'inbound'
                          ? <FiPhoneIncoming size={14} color="#10b981" />
                          : <FiPhoneOutgoing size={14} color="#3b82f6" />}
                      </td>
                      <td style={{ fontSize: 13 }}>
                        {n.contact || <span style={{ fontFamily: 'monospace' }}>{n.number}</span>}
                      </td>
                      <td style={{ fontSize: 13, color: 'var(--text-muted)' }}>{n.operator}</td>
                      <td style={{ fontSize: 13, whiteSpace: 'nowrap' }}>{formatDuration(n.sec)}</td>
                      <td><span className="badge badge-neutral" style={{ whiteSpace: 'nowrap' }}>{n.theme_label}</span></td>
                      <td style={{ fontSize: 13 }}>
                        {n.text}
                        {n.decoded && (
                          <div style={{ color: '#f59e0b', fontSize: 12 }}>→ {n.decoded}</div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {notes.length > 0 && (
              <Pager page={curPage} totalPages={totalPages} total={notes.length}
                from={(curPage - 1) * PER_PAGE + 1} to={Math.min(curPage * PER_PAGE, notes.length)}
                onPage={setPage} truncated={data.notes_truncated} />
            )}
          </div>
        </>
      )}
    </div>
  );
}

function Header({ days, setDays, onReload, updatedAt }) {
  return (
    <div className="page-header">
      <div>
        <h1 className="page-title">Izohlar tahlili</h1>
        <p className="page-subtitle">
          Operatorlar qo'ng'iroqlarga yozgan izohlar: mavzular, xodimlar va yozuv sifati
          {updatedAt && ` · yangilandi ${updatedAt.toLocaleTimeString('uz-UZ', { hour: '2-digit', minute: '2-digit' })}`}
        </p>
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        {PERIODS.map((p) => (
          <button key={p.v} className={`btn btn-sm ${days === p.v ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setDays(p.v)}>{p.t}</button>
        ))}
        <button className="btn btn-sm btn-ghost" onClick={onReload} title="Yangilash">
          <FiRefreshCw size={15} />
        </button>
      </div>
    </div>
  );
}

// Sahifalagich: ko'p sahifa bo'lsa boshi/oxiri va joriy sahifa atrofi ko'rsatiladi.
function Pager({ page, totalPages, total, from, to, onPage, truncated }) {
  const nums = [];
  const add = (n) => { if (n >= 1 && n <= totalPages && !nums.includes(n)) nums.push(n); };
  add(1); add(2);
  for (let i = page - 1; i <= page + 1; i++) add(i);
  add(totalPages - 1); add(totalPages);
  nums.sort((a, b) => a - b);

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
      padding: 12, borderTop: '1px solid var(--border,#2d3a4f)' }}>
      <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
        {from}–{to} / {total} ta izoh
        {truncated && ' (serverdan eng yangi 3000 tasi olindi)'}
      </span>
      <div style={{ flex: 1 }} />
      <button className="btn btn-sm btn-secondary" disabled={page <= 1} onClick={() => onPage(page - 1)}>
        <FiChevronLeft size={14} /> Oldingi
      </button>
      {nums.map((n, i) => (
        <React.Fragment key={n}>
          {i > 0 && nums[i - 1] !== n - 1 && <span style={{ color: 'var(--text-muted)' }}>…</span>}
          <button className={`btn btn-sm ${n === page ? 'btn-primary' : 'btn-ghost'}`}
            style={{ minWidth: 34, justifyContent: 'center' }} onClick={() => onPage(n)}>{n}</button>
        </React.Fragment>
      ))}
      <button className="btn btn-sm btn-secondary" disabled={page >= totalPages} onClick={() => onPage(page + 1)}>
        Keyingi <FiChevronRight size={14} />
      </button>
    </div>
  );
}

function Stat({ label, value, hint }) {
  return (
    <div className="stat-card">
      <span className="stat-label">{label}</span>
      <span className="stat-value">{value}</span>
      {hint && <span className="stat-sub">{hint}</span>}
    </div>
  );
}

function QualityBox({ title, main, sub }) {
  return (
    <div style={{ background: 'var(--bg-primary,#0f172a)', borderRadius: 10, padding: 14 }}>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>{title}</div>
      <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 6 }}>{main}</div>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5 }}>{sub}</div>
    </div>
  );
}

// Raqamlardan kelib chiqadigan qisqa kuzatuvlar — har safar qayta hisoblanadi.
function buildHints(totals, quality, themes) {
  const out = [];
  if (totals.coverage_pct < 50) {
    out.push(`Qo'ng'iroqlarning faqat ${totals.coverage_pct}% iga izoh yozilgan `
      + `(${totals.notes} / ${totals.total_calls}). Takrorlanuvchi muammolar to'liq ko'rinmaydi.`);
  }
  if (quality.cyrillic > 0 && quality.latin > 0) {
    out.push(`Yozuv ikki alifboda: ${quality.cyrillic} kirill, ${quality.latin} lotin. `
      + `Bitta alifboga kelishilsa, qidiruv va hisobot aniq bo'ladi.`);
  }
  if (quality.short_count > 0) {
    out.push(`${quality.short_count} ta izoh 15 belgidan qisqa — ulardan keyin boshqa xodim nima `
      + `qilinganini tushunmaydi.`);
  }
  if (quality.layout_bad_count > 0) {
    out.push(`${quality.layout_bad_count} ta izoh klaviatura tili xato yoqilgan holda yozilgan.`);
  }
  const top = themes[0];
  if (top && top.pct >= 15) {
    out.push(`Eng ko'p mavzu — "${top.label}" (${top.count} ta, ${top.pct}%). `
      + `Bilim bazasida tayyor yo'riqnoma bo'lsa, qo'ng'iroq vaqti qisqaradi.`);
  }
  return out;
}

const tooltipStyle = { background: '#1e293b', border: '1px solid #2d3a4f', borderRadius: 8, color: '#f1f5f9' };
const emptyCell = { textAlign: 'center', padding: 30, color: 'var(--text-muted)' };
const codeStyle = { background: 'var(--bg-primary,#0f172a)', padding: '2px 6px', borderRadius: 4, fontSize: 13 };
